import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import DeleteActionButton from '../../../../../components/designe/ActionButtons/DeleteActionButton';
import EditActionButton from '../../../../../components/designe/ActionButtons/EditActionButton';
import ConfirmDialog from '../../../../../components/reusable/confirm-dialog/ConfirmDialog';
import { useBulkDeleteTrainingSets } from '../../../../../redux/api/trainingsAPI';
import { IExerciseOption } from '../../../../../types/interfaces/exercise-option';
import { ITrainingSet } from '../../../../../types/interfaces/training-set';
import { uniqBy } from '../../../../../utils/collections/array-utils';
import { getSetKey } from '../../../../../utils/training/set-utils';
import FinishedTrainingSetList from '../finished-training-set-list/FinishedTrainingSetList';
import { useStyles } from './FinishedTrainingSetGroup.styles';

function FinishedTrainingSetGroup({
  trainingSetGroup,
  exerciseOptions,
  trainingId,
  canEdit = true,
  onEditSetGroupClick,
}: {
  trainingSetGroup: ITrainingSet[];
  exerciseOptions: IExerciseOption[];
  trainingId: string;
  canEdit?: boolean;
  onEditSetGroupClick: (trainingSetGroup: ITrainingSet[]) => void;
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [setGroupDeleting, setSetGroupDeleting] = useState(false);
  const [bulkDeleteTrainingSets] = useBulkDeleteTrainingSets();

  const { classes } = useStyles(theme);
  const isSuperset = trainingSetGroup.length > 1;
  const setNumber = trainingSetGroup[0]?.setNumber;

  async function onRemoveSetGroup() {
    if (!setNumber) return;
    try {
      await bulkDeleteTrainingSets({
        trainingId,
        setNumber,
      }).unwrap();
      setSetGroupDeleting(false);
    } catch (error) {
      console.error(error);
    }
  }

  const viewName = useMemo(() => {
    const uniqueExerciseTitles = uniqBy<ITrainingSet>(
      trainingSetGroup,
      'exerciseId',
    )
      .map((set) => set.exercise?.title)
      .filter(Boolean)
      .join(', ');

    if (!uniqueExerciseTitles) return null;

    return (
      <Typography variant="h3" color={theme.palette.font.secondary}>
        {isSuperset && (
          <Typography
            variant="h3"
            fontWeight={600}
            color={theme.palette.font.secondary}
            component="span"
            marginRight={0.5}>
            {t('setTable.superset')}:
          </Typography>
        )}
        {uniqueExerciseTitles}
      </Typography>
    );
  }, [trainingSetGroup, theme.palette.font.secondary, isSuperset, t]);

  return (
    <Box
      className={classes.trainingSetGroup}
      key={getSetKey(trainingSetGroup[0])}>
      {canEdit && (
        <Box className={classes.setGroupHeader}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center">
            {viewName}
            <Box display="flex" gap={1}>
              <EditActionButton
                onClick={() => onEditSetGroupClick(trainingSetGroup)}
              />
              <DeleteActionButton onClick={() => setSetGroupDeleting(true)} />
            </Box>
          </Box>
        </Box>
      )}
      {trainingSetGroup.map((trainingSet: ITrainingSet) => (
        <FinishedTrainingSetList
          trainingSet={trainingSet}
          exerciseOptions={exerciseOptions}
          trainingId={trainingId}
          canEdit={canEdit}
          key={getSetKey(trainingSet)}
        />
      ))}
      <ConfirmDialog
        open={setGroupDeleting}
        onClose={() => setSetGroupDeleting(false)}
        title={t('common.delete')}
        onSubmit={onRemoveSetGroup}>
        {t('common.deleteQuestion')}
      </ConfirmDialog>
    </Box>
  );
}

export default FinishedTrainingSetGroup;
